import React, { useMemo } from 'react';
import { CheckCircle, Globe } from 'lucide-react';
import { HeroImage } from '../common';
import { UnsplashCredit } from '../common/UnsplashCredit';
import { ItineraryListItem } from '../../types';
import WorldMap, { WorldMapDestination } from './WorldMap';
import './DestinationPassport.css';

interface DestinationStamp {
  destination: string;
  visits: number;
  totalDays: number;
  lastVisit: string;
  heroImageUrl?: string;
  /** Who took `heroImageUrl`. Carried with it, because the licence binds on
   *  the display and this stamp is one (r774 finding 2). */
  heroAttribution?: { photographer: string; unsplash_link: string } | null;
  lat?: number;
  lng?: number;
}

interface DestinationPassportProps {
  plans: ItineraryListItem[];
  defaultShowMap?: boolean;
  showAllOnMap?: boolean; // Include upcoming + dreaming destinations on map
}

function tripDays(start: string | null, end: string | null): number {
  if (!start || !end) return 1;
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1);
}

function buildStamps(plans: ItineraryListItem[]): DestinationStamp[] {
  const todayStr = new Date().toISOString().split('T')[0];
  const pastTrips = plans.filter(
    p => p.destination && ((p.end_date && p.end_date < todayStr) || p.lifecycle_state === 'completed')
  );

  const map = new Map<string, DestinationStamp>();
  for (const p of pastTrips) {
    const dest = p.destination!;
    const key = dest.toLowerCase().trim();
    const existing = map.get(key);
    const days = tripDays(p.start_date, p.end_date);
    if (existing) {
      existing.visits += 1;
      existing.totalDays += days;
      if (p.end_date && p.end_date > existing.lastVisit) {
        existing.lastVisit = p.end_date;
      }
      if (!existing.heroImageUrl && p.hero_image_url) {
        existing.heroImageUrl = p.hero_image_url;
        // The credit travels WITH the url it belongs to — taking one without
        // the other is how the stamp came to show a photograph and name
        // nobody.
        existing.heroAttribution = p.hero_attribution ?? null;
      }
      if (existing.lat == null && p.destination_lat != null) {
        existing.lat = p.destination_lat;
        existing.lng = p.destination_lng;
      }
    } else {
      map.set(key, {
        destination: dest,
        visits: 1,
        totalDays: days,
        lastVisit: p.end_date || p.start_date || p.created_at,
        heroImageUrl: p.hero_image_url,
        heroAttribution: p.hero_attribution ?? null,
        lat: p.destination_lat,
        lng: p.destination_lng,
      });
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.lastVisit).getTime() - new Date(a.lastVisit).getTime()
  );
}

function formatStampDate(dateStr: string): string {
  const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

const DestinationPassport: React.FC<DestinationPassportProps> = ({ plans, defaultShowMap = false, showAllOnMap = false }) => {
  const stamps = buildStamps(plans);
  const [showMap, setShowMap] = React.useState(defaultShowMap);

  const totalDays = useMemo(() => stamps.reduce((s, st) => s + st.totalDays, 0), [stamps]);

  const mapDestinations = useMemo<WorldMapDestination[]>(() => {
    if (!showAllOnMap) {
      // Original: only visited destinations
      return stamps
        .filter((s): s is DestinationStamp & { lat: number; lng: number } =>
          s.lat != null && s.lng != null
        )
        .map(s => ({ name: s.destination, lat: s.lat, lng: s.lng, visits: s.visits, status: 'visited' as const }));
    }

    // All destinations: visited + upcoming + dreaming
    const todayStr = new Date().toISOString().split('T')[0];
    const visitedKeys = new Set(stamps.map(s => s.destination.toLowerCase().trim()));
    const all: WorldMapDestination[] = stamps
      .filter((s): s is DestinationStamp & { lat: number; lng: number } =>
        s.lat != null && s.lng != null
      )
      .map(s => ({ name: s.destination, lat: s.lat, lng: s.lng, visits: s.visits, status: 'visited' as const }));

    for (const p of plans) {
      if (!p.destination || visitedKeys.has(p.destination.toLowerCase().trim())) continue;
      if (p.destination_lat == null || p.destination_lng == null) continue;
      const key = p.destination.toLowerCase().trim();
      if (all.some(a => a.name.toLowerCase().trim() === key)) continue;

      const isInProgress = p.start_date && p.start_date <= todayStr && (!p.end_date || p.end_date >= todayStr);
      const isUpcoming = p.start_date && p.start_date > todayStr;
      const status = isInProgress ? 'upcoming' as const : isUpcoming ? 'upcoming' as const : 'dreaming' as const;
      all.push({ name: p.destination, lat: p.destination_lat, lng: p.destination_lng, visits: 0, status });
      visitedKeys.add(key); // prevent duplicates
    }
    return all;
  }, [stamps, plans, showAllOnMap]);

  if (stamps.length === 0 && mapDestinations.length === 0) return null;

  return (
    <div className="passport-section">
      <div className="passport-header">
        <h3 className="passport-title">
          <CheckCircle size={16} />
          {showAllOnMap ? 'Destinations' : 'Destinations Visited'}
        </h3>
        <div className="passport-header-right">
          {mapDestinations.length > 0 && (
            <button
              className={`passport-map-toggle ${showMap ? 'active' : ''}`}
              onClick={() => setShowMap(v => !v)}
            >
              <Globe size={14} />
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          )}
          <span className="passport-total-days">{totalDays}d</span>
        </div>
      </div>

      {showMap && <WorldMap destinations={mapDestinations} />}

      <div className="passport-stamps">
        {stamps.map((stamp) => (
          <div key={stamp.destination} className="passport-stamp">
            <HeroImage
              src={stamp.heroImageUrl}
              fallbackKey={stamp.destination}
              size="thumb-sm"
            />
            <div className="stamp-body">
              <span className="stamp-destination">{stamp.destination}</span>
              <span className="stamp-subtitle">
                {stamp.visits} visit{stamp.visits !== 1 ? 's' : ''} &middot; {stamp.totalDays}d &middot; Last {formatStampDate(stamp.lastVisit)}
              </span>
            </div>
            <span className={`stamp-badge ${stamp.visits >= 2 ? 'stamp-badge--accent' : 'stamp-badge--success'}`}>
              {stamp.visits}
            </span>
            {/* The stamp is a div, not a button, so the credit nests here
                rather than needing a frame around it. */}
            {stamp.heroImageUrl && stamp.heroAttribution && (
              <UnsplashCredit
                photographer={stamp.heroAttribution.photographer}
                profileLink={stamp.heroAttribution.unsplash_link}
                className="card-photo-credit"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DestinationPassport;
